import { useEffect, useRef, useState } from 'react';
import type { DocPage } from '../types';
import { COULDNT_REASONS, FAX_ADDRESS } from '../utils';

function Backdrop({ label, onClose, narrow, children }: { label: string; onClose: () => void; narrow?: boolean; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input, button, select, textarea')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  return (
    <div className="dc-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className={`dc-dialog${narrow ? ' dc-dialog-460' : ''}`} role="dialog" aria-modal="true" aria-label={label}>
        {children}
      </div>
    </div>
  );
}

/** Fax set-up (EOB_HANDOFF §6.1), address renamed for Document Center. */
export function FaxDialog({ onClose, onTest }: { onClose: () => void; onTest: () => void }) {
  return (
    <Backdrop label="Set up fax" onClose={onClose}>
      <h2 className="dc-dialog-title">Set up your fax</h2>
      <p className="dc-meta" style={{ margin: 0 }}>Forward your fax line to the address below. Everything faxed to it arrives here, read and sent where it belongs.</p>
      <div className="dc-notice"><strong style={{ color: 'var(--fg)' }}>{FAX_ADDRESS}</strong></div>
      <p className="dc-meta" style={{ margin: 0 }}>Most fax providers let you forward to email in their settings. ELVA can send a test fax to check it’s working.</p>
      <div className="dc-dialog-actions">
        <button type="button" className="dc-btn dc-btn-primary" onClick={onTest}>Send a test fax</button>
        <button type="button" className="dc-btn" onClick={onClose}>Done</button>
      </div>
    </Backdrop>
  );
}

/** "Couldn't charge it" (D200): why, then ELVA asks the payer to pay another way. */
export function CouldntChargeDialog({ payer, onClose, onSend }: {
  payer: string;
  onClose: () => void;
  onSend: (reason: (typeof COULDNT_REASONS)[number]['value'], note: string) => void;
}) {
  const [reason, setReason] = useState<(typeof COULDNT_REASONS)[number]['value']>('used');
  const [note, setNote] = useState('');
  return (
    <Backdrop label={`Why couldn’t you charge ${payer}’s card?`} onClose={onClose} narrow>
      <h2 className="dc-dialog-title" style={{ fontSize: 20 }}>Why couldn’t you charge {payer}’s card?</h2>
      <fieldset className="dc-radios">
        <legend className="dc-vh">Reason</legend>
        {COULDNT_REASONS.map((r) => (
          <label key={r.value} className="dc-opt">
            <input type="radio" name="couldnt-reason" checked={reason === r.value} onChange={() => { setReason(r.value); }} />
            <span>{r.label}</span>
          </label>
        ))}
        {reason === 'other' ? (
          <label className="dc-flabel">
            <span className="dc-vh">One-line note</span>
            <input className="dc-field" value={note} maxLength={120} placeholder="One line about what happened" onChange={(e) => { setNote(e.target.value); }} />
          </label>
        ) : null}
      </fieldset>
      <p className="dc-meta" style={{ margin: 0 }}>ELVA asks {payer} to pay another way, by check or EFT, and tells you what {payer} says.</p>
      <div className="dc-dialog-actions" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="dc-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="dc-btn dc-btn-primary" onClick={() => { onSend(reason, note); }}>Send to ELVA</button>
      </div>
    </Backdrop>
  );
}

/** Upload panel (EOB_HANDOFF §6.2). */
export function UploadDialog({ onClose, onUploaded }: { onClose: () => void; onUploaded: (n: number) => void }) {
  const [count, setCount] = useState(0);
  return (
    <Backdrop label="Upload documents" onClose={onClose}>
      <h2 className="dc-dialog-title">Upload documents</h2>
      <label className="dc-drop" style={{ display: 'block', cursor: 'pointer' }}>
        {count > 0 ? `${String(count)} file${count === 1 ? '' : 's'} ready` : <>Drop EOBs, letters or scans here, or <span className="dc-src">choose files</span></>}
        <input type="file" accept="image/*,.pdf,.tiff,.heic" multiple className="dc-vh" onChange={(e) => { setCount(e.target.files?.length ?? 0); }} />
      </label>
      <p className="dc-meta" style={{ margin: 0 }}>ELVA reads each one, works out what it is, and sends it where it belongs — the same as a fax.</p>
      <div className="dc-dialog-actions">
        <button type="button" className="dc-btn dc-btn-primary" disabled={count === 0} onClick={() => { onUploaded(count); }}>Upload</button>
        <button type="button" className="dc-btn" onClick={onClose}>Cancel</button>
      </div>
    </Backdrop>
  );
}

/** Full-page scan viewer (EOB_HANDOFF §5) — text only (D108). */
export function PageViewer({ pages, title, onClose }: { pages: DocPage[]; title: string; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  return (
    <div className="dc-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className="dc-viewer" role="dialog" aria-modal="true" aria-label={`${title} — full page`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h2 className="dc-h3" style={{ fontSize: 17 }}>{title}</h2>
          <button type="button" className="dc-btn dc-btn-sm" onClick={onClose}>Close</button>
        </div>
        {pages.map((p, i) => (
          <figure key={`pg-${String(i)}`} style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="dc-scan" role="img" aria-label={p.alt}>{renderScan(p.text)}</div>
            <figcaption className="dc-meta">{p.caption}</figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

/** Render the scan text, turning ⟪smudged⟫ markers into blurred spans. */
export function renderScan(text: string): React.ReactNode {
  const parts = text.split(/(⟪smudged⟫)/g);
  return parts.map((part, i) =>
    part === '⟪smudged⟫'
      ? <span key={`s-${String(i)}`} className="dc-smudge">  00.00</span>
      : <span key={`t-${String(i)}`}>{part}</span>,
  );
}
